"use client";
import { useEffect, useId, useRef, useSyncExternalStore } from "react";
import useSWR from "swr";
import { getVersion, queryKey, subscribeVersions } from "./cache";
import type { GraphqlError, GraphqlResult } from "./GraphqlResult";
import toGraphqlError from "./toGraphqlError";

export {
  clearQueryCache,
  invalidateQueries,
  updateQueryCache,
} from "./cache";

/**
 * A `.client` getter: `getXQueryClient(variables)`.
 */
type Fetcher<Data, Variables> = (
  variables: Variables,
) => Promise<GraphqlResult<Data>>;

/**
 * Apollo's vocabulary, same meaning:
 * - `cache-first` (default): a cached result is used and not refetched until
 *   it is invalidated or `refetch()` is called.
 * - `cache-and-network`: a cached result renders immediately while a request
 *   refreshes it.
 * - `network-only`: always requests on mount; the result is cached for other
 *   readers (a cached result shows meanwhile).
 * - `no-cache`: always requests and shares nothing with other hooks.
 */
export type FetchPolicy =
  | "cache-first"
  | "cache-and-network"
  | "network-only"
  | "no-cache";

type Options<Variables> = {
  /** Render without fetching; `data` stays undefined until `skip` turns false. */
  skip?: boolean;
  fetchPolicy?: FetchPolicy;
} & (Record<string, never> extends Variables
  ? { variables?: Variables }
  : { variables: Variables });

/**
 * `[options]` when the operation has required variables, `[options?]`
 * otherwise. The generated `useXQuery` hooks take these.
 */
export type QueryArgs<Variables> =
  Record<string, never> extends Variables
    ? [options?: Options<Variables>]
    : [options: Options<Variables>];

// Mount-time revalidations of the same key within this window share one
// request (siblings mounting together); `refetch()` is never deduplicated.
const DEDUPING_INTERVAL = 100;

/**
 * Runs a `.client` query from a client component, on SWR. Prefer the
 * generated `useXQuery` hook of the operation, which binds the getter and the
 * types.
 *
 * Results are cached per operation and variables for the page's lifetime
 * (see `cache.ts`); `fetchPolicy` says how the cache is used. After a mutation
 * that changes what a query reads, pass it in the mutation's `refetchQueries`
 * or call `invalidateQueries`.
 *
 * @example
 * const { data, loading, refetch } = useRateQuery({
 *   variables: { from, to },
 *   skip: from === to,
 * });
 */
const useQuery = <Data, Variables extends object>(
  fetcher: Fetcher<Data, Variables>,
  ...[options]: QueryArgs<Variables>
) => {
  const skip = options?.skip ?? false;
  const fetchPolicy = options?.fetchPolicy ?? "cache-first";
  const instanceId = useId();
  // Re-renders (and re-keys) this hook when the operation is invalidated.
  useSyncExternalStore(
    subscribeVersions,
    () => getVersion(fetcher),
    () => getVersion(fetcher),
  );
  const key = skip
    ? null
    : queryKey(
        fetcher,
        options?.variables,
        fetchPolicy === "no-cache" ? instanceId : undefined,
      );

  // `keepPreviousData` keeps the result on screen while an invalidation or a
  // session change refetches, but new variables start from nothing, as with
  // Apollo: the previous result is shown only for the variables it answers.
  const variablesKey = JSON.stringify(options?.variables ?? {});
  const shownVariables = useRef<string | undefined>(undefined);

  const { data, error, isLoading, mutate } = useSWR<GraphqlResult<Data>>(
    key,
    ([getter, variables]: readonly [Fetcher<Data, Variables>, Variables]) =>
      getter(variables),
    {
      revalidateIfStale: fetchPolicy !== "cache-first",
      revalidateOnMount:
        fetchPolicy === "network-only" || fetchPolicy === "no-cache"
          ? true
          : undefined,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      keepPreviousData: true,
      dedupingInterval: DEDUPING_INTERVAL,
    },
  );

  const answersVariables =
    !isLoading || shownVariables.current === variablesKey;
  useEffect(() => {
    if (!skip && answersVariables) {
      shownVariables.current = variablesKey;
    }
  });

  if (skip) {
    return { data: undefined, loading: false, error: undefined, refetch };
  }

  async function refetch(): Promise<GraphqlResult<Data>> {
    return (await mutate()) ?? { data: undefined };
  }

  const result = answersVariables ? data : undefined;
  const failure: GraphqlError | undefined = error
    ? {
        message: error instanceof Error ? error.message : String(error),
        errors: [],
      }
    : result && toGraphqlError(result);

  return { data: result?.data, loading: isLoading, error: failure, refetch };
};

export default useQuery;
